// pages/AdminsPage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import API from '../api/MainApi';
import './AdminsPage.css';

const AdminsPage = () => {
    const { admin: currentAdmin } = useAuth();
    const { showConfirm, showAlert } = useModal();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [formData, setFormData] = useState({
        login: '',
        password: '',
        name: '',
        role: 'admin'
    });

    useEffect(() => {
        loadAdmins();
    }, []);

    const loadAdmins = async () => {
        try {
            const data = await API.getAdmins();
            setAdmins(data.data);
        } catch (error) {
            showAlert('Ошибка', 'Не удалось загрузить список администраторов', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        
        if (!formData.login || !formData.password) {
            showAlert('Ошибка', 'Логин и пароль обязательны', 'error');
            return;
        }

        showConfirm(
            'Создание администратора',
            `Создать администратора с логином "${formData.login}"?`,
            async () => {
                try {
                    await API.createAdmin(
                        formData.login,
                        formData.password,
                        formData.role,
                        formData.name
                    );
                    showAlert('Успешно', 'Администратор успешно создан', 'success');
                    setShowCreateForm(false);
                    setFormData({ login: '', password: '', name: '', role: 'admin' });
                    await loadAdmins();
                } catch (error) {
                    showAlert('Ошибка', error.response?.data?.message || 'Ошибка создания администратора', 'error');
                }
            }
        );
    };

    const handleDeleteAdmin = (id, login) => {
        if (id === currentAdmin.id) {
            showAlert('Ошибка', 'Нельзя удалить свой собственный аккаунт', 'error');
            return;
        }

        showConfirm(
            'Удаление администратора',
            `Вы уверены, что хотите удалить администратора "${login}"?`,
            async () => {
                try {
                    await API.deleteAdmin(id);
                    showAlert('Успешно', 'Администратор удален', 'success');
                    await loadAdmins();
                } catch (error) {
                    showAlert('Ошибка', 'Не удалось удалить администратора', 'error');
                }
            }
        );
    };

    const handleCancelCreate = () => {
        if (formData.login || formData.password || formData.name) {
            showConfirm(
                'Отмена',
                'Все введенные данные будут потеряны. Продолжить?',
                () => {
                    setShowCreateForm(false);
                    setFormData({ login: '', password: '', name: '', role: 'admin' });
                }
            );
        } else {
            setShowCreateForm(false);
        }
    };

    if (loading) return <div className="loading">Загрузка...</div>;

    return (
        <div className="admins-page">
            <div className="page-header">
                <h1>Управление администраторами</h1>
                <button 
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="btn btn-primary"
                >
                    {showCreateForm ? 'Отмена' : 'Создать администратора'}
                </button>
            </div>

            {showCreateForm && (
                <div className="create-form">
                    <h2>Новый администратор</h2>
                    <form onSubmit={handleCreateAdmin}>
                        <div className="form-group">
                            <label>Логин *</label>
                            <input
                                type="text"
                                name="login"
                                value={formData.login}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Пароль *</label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Имя</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="form-group">
                            <label>Роль</label>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleInputChange}
                            >
                                <option value="admin">Admin</option>
                                <option value="editor">Editor</option>
                                <option value="developer">Developer</option>
                                <option value="viewer">Viewer</option>
                                <option value="superadmin">Superadmin</option>
                            </select>
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                onClick={handleCancelCreate}
                                className="btn-cancel"
                            >
                                Отмена
                            </button>
                            <button type="submit" className="btn-submit">
                                Создать
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="admins-table">
                <table>
                    <thead>
                        <tr>
                            <th>Логин</th>
                            <th>Имя</th>
                            <th>Роль</th>
                            <th>Последний вход</th>
                            <th>Статус</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {admins.map(admin => (
                            <tr key={admin._id}>
                                <td>{admin.login}</td>
                                <td>{admin.name || '-'}</td>
                                <td>
                                    <span className={`role-badge role-${admin.role}`}>
                                        {admin.role}
                                    </span>
                                </td>
                                <td>
                                    {admin.lastLogin 
                                        ? new Date(admin.lastLogin).toLocaleString()
                                        : 'Никогда'
                                    }
                                </td>
                                <td>
                                    <span className={`status-badge ${admin.isActive ? 'active' : 'inactive'}`}>
                                        {admin.isActive ? 'Активен' : 'Неактивен'}
                                    </span>
                                </td>
                                <td>
                                    {admin._id !== currentAdmin.id && (
                                        <button
                                            onClick={() => handleDeleteAdmin(admin._id, admin.login)}
                                            className="btn-delete"
                                        >
                                            Удалить
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminsPage;